/**
 * Shared display formatters.
 *
 * Keep all display conversion logic here so individual components don't
 * each reinvent Intl.NumberFormat config.
 */

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const currencyFormatterPrecise = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const percentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Coerce a value that may be a number, numeric string, or null/undefined
 * to a JS number. Non-numeric input returns 0.
 */
export function toNumber(value: unknown): number {
  if (value == null) return 0;
  if (typeof value === "number") return Number.isNaN(value) ? 0 : value;
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n;
}

/**
 * Format a currency value. Accepts number, numeric string, or null/undefined.
 * Null/undefined renders as "--" so empty cells are visually distinct from $0.
 */
export function formatCurrency(value: unknown, options?: { precise?: boolean }): string {
  if (value == null || value === "") return "--";
  const n = toNumber(value);
  return options?.precise ? currencyFormatterPrecise.format(n) : currencyFormatter.format(n);
}

/**
 * Format a numeric ratio (0-1) as a whole-number percent. `0.42` → `"42%"`.
 */
export function formatPercent(value: unknown): string {
  if (value == null || value === "") return "--";
  return percentFormatter.format(toNumber(value));
}
